import type {
  DesignSessionState,
  VisualizationAgentEvent,
} from '../contracts/index.ts';
import type {
  AgentMessageInput,
  ProjectApplyResult,
  VisualizationAgentClient,
} from './VisualizationAgentClient.ts';
import { API_BASE_URL, getStoredAccessToken, refreshAccessToken } from '@/api/request';
import { visualizationAgentEventSchema } from '../contracts/index.ts';

interface AgentErrorPayload {
  code?: string;
  message?: string;
}

export interface HttpVisualizationAgentClientOptions {
  baseUrl?: string;
  fetch?: typeof globalThis.fetch;
}

function joinUrl(baseUrl: string, path: string): string {
  return `${baseUrl.replace(/\/$/, '')}/${path.replace(/^\//, '')}`;
}

function authHeaders(token: string | null): Record<string, string> {
  return token ? { Authorization: `Bearer ${token}` } : {};
}

async function readError(response: Response): Promise<Error> {
  let payload: AgentErrorPayload | null = null;
  try {
    payload = await response.json() as AgentErrorPayload;
  }
  catch {
    // The response may be an HTML gateway error rather than a JSON API error.
  }
  const message = payload?.message?.trim()
    || `AI 服务请求失败（HTTP ${response.status}）`;
  const error = new Error(message);
  error.name = payload?.code || 'AgentHttpError';
  return error;
}

function eventFromDataLines(dataLines: string[]): VisualizationAgentEvent | null {
  if (!dataLines.length)
    return null;
  const payload = dataLines.join('\n');
  if (!payload.trim())
    return null;
  return visualizationAgentEventSchema.parse(JSON.parse(payload));
}

export class HttpVisualizationAgentClient implements VisualizationAgentClient {
  private readonly baseUrl: string;
  private readonly fetchImpl: typeof globalThis.fetch;
  private activeTurn: { id: string; controller: AbortController } | null = null;
  private cancellationBarrier: Promise<void> = Promise.resolve();
  private requestGeneration = 0;

  constructor(options: HttpVisualizationAgentClientOptions = {}) {
    this.baseUrl = options.baseUrl ?? API_BASE_URL;
    this.fetchImpl = options.fetch ?? globalThis.fetch.bind(globalThis);
  }

  async* sendMessage(
    input: AgentMessageInput,
  ): AsyncIterable<VisualizationAgentEvent> {
    this.stop();
    const requestGeneration = ++this.requestGeneration;
    await this.cancellationBarrier;
    if (requestGeneration !== this.requestGeneration)
      return;

    const controller = new AbortController();
    this.activeTurn = { id: input.turnId, controller };

    try {
      let response = await this.openStream(input, controller, getStoredAccessToken());
      if (response.status === 401) {
        const token = await refreshAccessToken();
        response = await this.openStream(input, controller, token);
      }
      if (!response.ok)
        throw await readError(response);
      if (!response.body)
        throw new Error('当前浏览器不支持流式 AI 响应。');

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let dataLines: string[] = [];

      while (true) {
        const { done, value } = await reader.read();
        buffer += decoder.decode(value, { stream: !done });
        const lines = buffer.split(/\r?\n/);
        buffer = done ? '' : (lines.pop() ?? '');

        for (const line of lines) {
          if (line === '') {
            const event = eventFromDataLines(dataLines);
            dataLines = [];
            if (event)
              yield event;
            continue;
          }
          if (line.startsWith('data:'))
            dataLines.push(line.slice(5).replace(/^ /, ''));
        }

        if (done) {
          const event = eventFromDataLines(dataLines);
          if (event)
            yield event;
          break;
        }
      }
    }
    finally {
      if (this.activeTurn?.id === input.turnId)
        this.activeTurn = null;
    }
  }

  stop(): void {
    this.requestGeneration += 1;
    const activeTurn = this.activeTurn;
    if (!activeTurn)
      return;
    this.activeTurn = null;
    activeTurn.controller.abort();
    const cancellationController = new AbortController();
    const timeoutId = setTimeout(() => cancellationController.abort(), 2000);
    const cancellation = this.cancelTurn(
      activeTurn.id,
      cancellationController.signal,
    ).catch(() => undefined).finally(() => {
      clearTimeout(timeoutId);
    });
    this.cancellationBarrier = Promise.all([
      this.cancellationBarrier,
      cancellation,
    ]).then(() => undefined);
  }

  async loadSession(_id: string): Promise<DesignSessionState> {
    throw new Error('AI 可视化会话持久化尚未开放。');
  }

  async reportApplyResult(result: ProjectApplyResult): Promise<void> {
    let token = getStoredAccessToken();
    let response = await this.fetchImpl(
      joinUrl(
        this.baseUrl,
        `/api/ai-visualization/agent/turns/${encodeURIComponent(result.turnId)}/apply-result`,
      ),
      {
        method: 'POST',
        headers: {
          ...authHeaders(token),
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(result),
      },
    );
    if (response.status === 401) {
      token = await refreshAccessToken();
      response = await this.fetchImpl(
        joinUrl(
          this.baseUrl,
          `/api/ai-visualization/agent/turns/${encodeURIComponent(result.turnId)}/apply-result`,
        ),
        {
          method: 'POST',
          headers: {
            ...authHeaders(token),
            'Accept': 'application/json',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(result),
        },
      );
    }
    if (!response.ok)
      throw await readError(response);
  }

  private openStream(
    input: AgentMessageInput,
    controller: AbortController,
    token: string | null,
  ): Promise<Response> {
    return this.fetchImpl(
      joinUrl(this.baseUrl, '/api/ai-visualization/agent/messages/stream'),
      {
        method: 'POST',
        headers: {
          ...authHeaders(token),
          'Accept': 'text/event-stream',
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify(input),
        signal: controller.signal,
      },
    );
  }

  private async cancelTurn(turnId: string, signal: AbortSignal): Promise<void> {
    const url = joinUrl(
      this.baseUrl,
      `/api/ai-visualization/agent/turns/${encodeURIComponent(turnId)}`,
    );
    const request = (token: string | null) => this.fetchImpl(url, {
      method: 'DELETE',
      headers: authHeaders(token),
      keepalive: true,
      signal,
    });

    let response = await request(getStoredAccessToken());
    if (response.status === 401)
      response = await request(await refreshAccessToken());
    if (!response.ok && response.status !== 404)
      throw await readError(response);
  }
}
